import React, { useState, useEffect } from 'react';
import './CalendarEditModal.css';

const CalendarEditModal = ({
    isOpen,
    onClose,
    event,
    onSave,
    resourceDataSource
}) => {
    const [formData, setFormData] = useState({
        Subject: '',
        StartTime: '',
        EndTime: '',
        Description: '',
        Location: '',
        FamilyId: 1,
        IsAllDay: false
    });

    // Helper function to format date for datetime-local input (local time)
    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        // Get local time components
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    useEffect(() => {
        if (event) {
            // Editing existing event
            setFormData({
                Subject: event.Subject || '',
                StartTime: formatDateForInput(event.StartTime),
                EndTime: formatDateForInput(event.EndTime),
                Description: event.Description || '',
                Location: event.Location || '',
                FamilyId: event.FamilyId || 1,
                IsAllDay: event.IsAllDay || false
            });
        } else {
            // Creating new event
            const now = new Date();
            const startTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
            const endTime = new Date(startTime.getTime() + 30 * 60 * 1000); // 30 minutes after start time

            setFormData({
                Subject: '',
                StartTime: formatDateForInput(startTime),
                EndTime: formatDateForInput(endTime),
                Description: '',
                Location: '',
                FamilyId: 1,
                IsAllDay: false
            });
        }
    }, [event]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            // If start time changes, automatically adjust end time
            if (name === 'StartTime' && value) {
                const startTime = new Date(value);

                if (newData.IsAllDay) {
                    // For full day events, set end time to 23:00 of the same day
                    const endOfDay = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate(), 23, 0);
                    newData.EndTime = formatDateForInput(endOfDay);
                } else if (!event) {
                    // For new events, set end time to 30 minutes later
                    const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);
                    newData.EndTime = formatDateForInput(endTime);
                }
            }

            // If end time is before start time, adjust it to be 30 minutes after start time
            if (name === 'EndTime' && newData.StartTime && newData.EndTime) {
                const startTime = new Date(newData.StartTime);
                const endTime = new Date(newData.EndTime);
                if (endTime <= startTime) {
                    const adjustedEndTime = new Date(startTime.getTime() + 30 * 60 * 1000);
                    newData.EndTime = formatDateForInput(adjustedEndTime);
                }
            }

            // Handle full day events using Syncfusion logic
            if (name === 'IsAllDay' && checked) {
                // For Syncfusion all-day events, set start to 00:00 and end to 23:59 of the same day
                if (newData.StartTime) {
                    const startDate = new Date(newData.StartTime);
                    const startOfDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0);
                    const endOfDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 23, 59);

                    newData.StartTime = formatDateForInput(startOfDay);
                    newData.EndTime = formatDateForInput(endOfDay);
                }
            } else if (name === 'IsAllDay' && !checked) {
                // When unchecking all-day, set reasonable default times
                if (newData.StartTime) {
                    const startDate = new Date(newData.StartTime);
                    const startOfDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 9, 0);
                    const endOfDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 10, 0);

                    newData.StartTime = formatDateForInput(startOfDay);
                    newData.EndTime = formatDateForInput(endOfDay);
                }
            }

            return newData;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const eventData = {
                ...formData,
                StartTime: new Date(formData.StartTime).toISOString(),
                EndTime: new Date(formData.EndTime).toISOString()
            };

            if (event) {
                // Update existing event
                await onSave(event.id, eventData);
            } else {
                // Create new event
                await onSave(null, eventData);
            }

            onClose();
        } catch (error) {
            console.error('Error saving event:', error);
            alert('Error saving event. Please try again.');
        }
    };

    const handleDelete = async () => {
        if (!event) return;

        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await onSave(event.id, null, true); // true indicates delete
                onClose();
            } catch (error) {
                console.error('Error deleting event:', error);
                alert('Error deleting event. Please try again.');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{event ? 'Edit Event' : 'Add New Event'}</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="event-form">
                    <div className="form-group">
                        <label htmlFor="Subject">Event Title *</label>
                        <input
                            type="text"
                            id="Subject"
                            name="Subject"
                            value={formData.Subject}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter event title"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="StartTime">Start Time *</label>
                            <input
                                type="datetime-local"
                                id="StartTime"
                                name="StartTime"
                                value={formData.StartTime}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="EndTime">End Time *</label>
                            <input
                                type="datetime-local"
                                id="EndTime"
                                name="EndTime"
                                value={formData.EndTime}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="FamilyId">Family Member</label>
                        <select
                            id="FamilyId"
                            name="FamilyId"
                            value={formData.FamilyId}
                            onChange={handleInputChange}
                        >
                            {resourceDataSource.map((family, index) => (
                                <option key={family.Id || `family-${index}`} value={family.Id}>
                                    {family.Name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="Location">Location</label>
                        <input
                            type="text"
                            id="Location"
                            name="Location"
                            value={formData.Location}
                            onChange={handleInputChange}
                            placeholder="Enter location"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="Description">Description</label>
                        <textarea
                            id="Description"
                            name="Description"
                            value={formData.Description}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="Enter event description"
                        />
                    </div>

                    <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="IsAllDay"
                                checked={formData.IsAllDay}
                                onChange={handleInputChange}
                            />
                            All Day Event
                        </label>
                    </div>

                    <div className="form-actions">
                        {event && (
                            <button
                                type="button"
                                className="delete-button"
                                onClick={handleDelete}
                            >
                                Delete Event
                            </button>
                        )}

                        <div className="action-buttons">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="save-button"
                            >
                                {event ? 'Update Event' : 'Create Event'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CalendarEditModal;
